// toolRouter.ts — rule-based dispatcher from query → MCP tool calls.
//
// Why rules instead of letting Qwen-1.5B do native function calling?
//   At 1.5B parameters, function-calling reliability is poor — invalid
//   JSON, wrong tool selection, missed parallelism. A deterministic
//   router built on the same intent classifier we already trust gives
//   reliable, predictable agent behaviour. The LLM stays focused on
//   composition; the router handles dispatch.
//
// This is the classic "tools as workflow" pattern (Anthropic) rather than
// "tools as agentic decision" (open agent). For a single-turn portfolio
// chat with a 1.5B local model, the workflow pattern is correct.

import type { Chunk } from "@/data/corpus";

export type PlannedToolCall = {
  name: string;
  args: Record<string, unknown>;
  reason: string; // why the router picked it (shown in trace)
};

// ─────────────────────────── pattern rules ───────────────────────────

const QUERY_RULES: Array<{
  match: RegExp;
  build: (q: string) => PlannedToolCall[];
}> = [
  {
    // MCP-JEST queries → parallel GitHub + npm
    match: /\bmcp.?jest\b/i,
    build: () => [
      {
        name: "getGithubRepo",
        args: { owner: "reallyartificial", repo: "mcp-jest" },
        reason: "MCP-JEST mentioned → fetch live GitHub stats",
      },
      {
        name: "getNpmPackage",
        args: { name: "mcp-jest" },
        reason: "MCP-JEST mentioned → fetch live npm downloads",
      },
    ],
  },
  {
    // aa-kit → PyPI
    match: /\baa.?kit\b/i,
    build: () => [
      {
        name: "getPyPIPackage",
        args: { name: "aa-kit" },
        reason: "aa-kit mentioned → fetch live PyPI version",
      },
    ],
  },
  {
    // 100LinesOfCode → GitHub
    match: /\b(100.?lines.?of.?code|100lines)\b/i,
    build: () => [
      {
        name: "getGithubRepo",
        args: { owner: "josharsh", repo: "100linesofCode" },
        reason: "100LinesOfCode → fetch live star count",
      },
    ],
  },
  {
    // Tongue → App Store
    match: /\btongue\b/i,
    build: () => [
      {
        name: "getAppStoreApp",
        args: { appId: "6759001870", country: "us" },
        reason: "Tongue mentioned → fetch live App Store data",
      },
    ],
  },
  {
    // Lockbox → App Store
    match: /\block.?box\b/i,
    build: () => [
      {
        name: "getAppStoreApp",
        args: { appId: "6747517926", country: "us" },
        reason: "Lockbox mentioned → fetch live App Store data",
      },
    ],
  },
  {
    // Plum iOS → App Store
    match: /\bplum.*(ios|app|iphone)\b|\b(ios|app|iphone).*plum\b/i,
    build: () => [
      {
        name: "getAppStoreApp",
        args: { appId: "1616851078", country: "in" },
        reason: "Plum iOS app referenced → fetch live App Store data",
      },
    ],
  },
  {
    // OpenAI roles → live job board. Handle plurals + verb-ing forms.
    match: /\bopenai\b.*\b(roles?|jobs?|positions?|hir(e|ing)|apply|target(ing|s)?)\b|\b(which|what)\b.*\broles?\b/i,
    build: (q) => {
      const filter = /codex/i.test(q)
        ? "codex"
        : /forward.?deployed/i.test(q)
          ? "forward deployed"
          : /startups?\b/i.test(q)
            ? "startups"
            : "deployment";
      return [
        {
          name: "findOpenAIRoles",
          args: { filter, limit: "6" },
          reason: `OpenAI roles → fetch live board (filter="${filter}")`,
        },
      ];
    },
  },
  {
    // Recent posts → live Substack feed
    match: /\b(recent|latest|new(est)?|what's he writing|what does he write|substack|unengineered|blog post)\b/i,
    build: () => [
      {
        name: "getSubstackPosts",
        args: { limit: "4" },
        reason: "Writing query → fetch live Unengineered feed",
      },
    ],
  },
  {
    // Pasted JD URL
    match: /https?:\/\/[^\s)]+/i,
    build: (q) => {
      const url = q.match(/https?:\/\/[^\s)]+/i)?.[0];
      if (!url) return [];
      if (/jobs\.ashbyhq|greenhouse|lever|workday|careers/i.test(url)) {
        return [
          {
            name: "analyzeJobDescription",
            args: { url },
            reason: "JD URL detected → fetch + extract",
          },
        ];
      }
      return [];
    },
  },
  {
    // OmniMCP → npm
    match: /\bomni.?mcp\b/i,
    build: () => [
      {
        name: "getNpmPackage",
        args: { name: "omnimcp" },
        reason: "OmniMCP mentioned → fetch live npm stats",
      },
    ],
  },
  {
    // ensure-json → npm
    match: /\bensure.?json\b/i,
    build: () => [
      {
        name: "getNpmPackage",
        args: { name: "ensure-json" },
        reason: "ensure-json mentioned → fetch live npm stats",
      },
    ],
  },
];

// ─────────────────────────── opportunistic from chunks ───────────────────────────

// If retrieved chunks contain hrefs to GitHub/npm/PyPI, suggest tool
// calls on the most-referenced ones. Bounded so we don't fire 20 calls.
function fromChunkHrefs(
  chunks: Chunk[],
  alreadyPlanned: PlannedToolCall[],
): PlannedToolCall[] {
  const extra: PlannedToolCall[] = [];
  const seen = new Set(alreadyPlanned.map((p) => `${p.name}:${JSON.stringify(p.args)}`));

  for (const chunk of chunks.slice(0, 3)) {
    // Only inspect the top-3 retrieved chunks to bound cost
    if (!chunk.href) continue;
    const gh = chunk.href.match(/github\.com\/([^/]+)\/([^/?#]+)/);
    if (gh) {
      const args = { owner: gh[1], repo: gh[2] };
      const k = `getGithubRepo:${JSON.stringify(args)}`;
      if (!seen.has(k)) {
        extra.push({
          name: "getGithubRepo",
          args,
          reason: `chunk ${chunk.id} cites ${gh[1]}/${gh[2]} on GitHub`,
        });
        seen.add(k);
      }
    }
    const npm = chunk.href.match(/npmjs\.com\/package\/([^/?#]+)/);
    if (npm) {
      const args = { name: npm[1] };
      const k = `getNpmPackage:${JSON.stringify(args)}`;
      if (!seen.has(k)) {
        extra.push({
          name: "getNpmPackage",
          args,
          reason: `chunk ${chunk.id} cites npm:${npm[1]}`,
        });
        seen.add(k);
      }
    }
    const pypi = chunk.href.match(/pypi\.org\/project\/([^/?#]+)/);
    if (pypi) {
      const args = { name: pypi[1] };
      const k = `getPyPIPackage:${JSON.stringify(args)}`;
      if (!seen.has(k)) {
        extra.push({
          name: "getPyPIPackage",
          args,
          reason: `chunk ${chunk.id} cites PyPI:${pypi[1]}`,
        });
        seen.add(k);
      }
    }
  }
  return extra.slice(0, 3); // bound the opportunistic batch
}

// ─────────────────────────── public ───────────────────────────

/**
 * Plan the tools that should fire for this query. Combines rule-based
 * pattern matching with opportunistic href-following from the retrieved
 * chunks. Returns at most ~5 calls — beyond that the trace gets noisy
 * and latency suffers.
 */
export function routeQuery(query: string, chunks: Chunk[]): PlannedToolCall[] {
  const planned: PlannedToolCall[] = [];
  for (const rule of QUERY_RULES) {
    if (rule.match.test(query)) {
      planned.push(...rule.build(query));
    }
  }
  // De-dupe (multiple rules might suggest the same call)
  const seen = new Set<string>();
  const unique = planned.filter((p) => {
    const k = `${p.name}:${JSON.stringify(p.args)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  // Augment with chunk-href-driven calls (only if we haven't already maxed out)
  if (unique.length < 5) {
    unique.push(...fromChunkHrefs(chunks, unique).slice(0, 5 - unique.length));
  }
  return unique.slice(0, 5);
}
